import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getUserTimezone, getClosestTimezone, getLocalizedTimezones } from '../../modules/timezone';

interface BorderlessTimezoneSelectorProps {
  value: string;
  onChange: (timezone: string) => void;
  className?: string;
  placeholder?: string;
  autoDetect?: boolean;
  disabled?: boolean;
  id?: string;
}

const BorderlessTimezoneSelector: React.FC<BorderlessTimezoneSelectorProps> = ({
  value,
  onChange,
  className = '',
  placeholder = 'Search timezones...',
  autoDetect = false,
  disabled = false,
  id
}) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Get localized timezones based on current language
  const TIMEZONES = useMemo(() => getLocalizedTimezones(), [i18n.language]);

  // Auto-detect user's timezone on mount if enabled
  useEffect(() => {
    if (autoDetect && !value) {
      const userTimezone = getUserTimezone();
      const closestTimezone = getClosestTimezone(userTimezone);
      onChange(closestTimezone);
    }
  }, [autoDetect, value, onChange]);

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsOpen(false);
        setSearchTerm('');
        setSelectedIndex(-1);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setSearchTerm('');
        setSelectedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen]);

  // Filter timezones based on search term
  const filteredTimezones = useMemo(() => {
    if (!searchTerm.trim()) return TIMEZONES;

    const term = searchTerm.toLowerCase();
    return TIMEZONES.filter(tz =>
      tz.label.toLowerCase().includes(term) ||
      tz.value.toLowerCase().includes(term)
    );
  }, [searchTerm, TIMEZONES]);

  // Get display label for selected timezone
  const selectedTimezone = TIMEZONES.find(tz => tz.value === value);
  const displayValue = selectedTimezone ? selectedTimezone.label : value;

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setSelectedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < filteredTimezones.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : filteredTimezones.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && filteredTimezones[selectedIndex]) {
          const newTimezone = filteredTimezones[selectedIndex].value;
          onChange(newTimezone);
          setIsOpen(false);
          setSearchTerm('');
          setSelectedIndex(-1);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
      case 'Tab':
        setIsOpen(false);
        setSearchTerm('');
        setSelectedIndex(-1);
        break;
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    if (isOpen && selectedIndex >= 0 && listRef.current) {
      const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex, isOpen]);

  // Handle timezone selection
  const handleSelect = (timezone: string) => {
    onChange(timezone);
    setIsOpen(false);
    setSearchTerm('');
    setSelectedIndex(-1);
    inputRef.current?.blur();
  };

  // Handle input focus
  const handleFocus = () => {
    // Only open if not already handling via mousedown
    // This handles keyboard navigation (Tab key)
    if (!isOpen) {
      setIsOpen(true);
      // Auto-select current timezone in the list
      const currentIndex = filteredTimezones.findIndex(tz => tz.value === value);
      if (currentIndex >= 0) {
        setSelectedIndex(currentIndex);
      }
    }
  };

  // Handle input mouse down to toggle dropdown
  const handleMouseDown = (e: React.MouseEvent) => {
    // Always prevent default to stop focus event from interfering
    e.preventDefault();

    if (isOpen) {
      // Close the dropdown
      setIsOpen(false);
      setSearchTerm('');
      setSelectedIndex(-1);
    } else {
      // Open the dropdown
      setIsOpen(true);
      // Auto-select current timezone in the list
      const currentIndex = filteredTimezones.findIndex(tz => tz.value === value);
      if (currentIndex >= 0) {
        setSelectedIndex(currentIndex);
      }
      // Focus the input for keyboard interaction
      inputRef.current?.focus();
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          id={id}
          value={searchTerm || displayValue}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={handleFocus}
          onMouseDown={handleMouseDown}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full py-2 pr-6 text-sm text-secondary-900 bg-transparent border-none outline-none focus:outline-none focus:ring-0 focus:border-transparent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ paddingLeft: '0', color: '#111827' }}
        />

        <div className="absolute inset-y-0 right-0 flex items-center pointer-events-none">
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-64 overflow-hidden">
          <div
            ref={listRef}
            className="max-h-64 overflow-y-auto"
            role="listbox"
          >
            {filteredTimezones.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                {t('availability:timezone.noTimezonesFound', 'No timezones found matching "{{searchTerm}}"', { searchTerm })}
              </div>
            ) : (
              filteredTimezones.map((timezone, index) => (
                <button
                  key={timezone.value}
                  onClick={() => handleSelect(timezone.value)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 focus:bg-primary-50 focus:outline-none transition-colors ${
                    index === selectedIndex
                      ? 'bg-primary-100 text-primary-700'
                      : timezone.value === value
                      ? 'bg-gray-100 text-gray-900 font-medium'
                      : 'text-gray-700'
                  }`}
                  role="option"
                  aria-selected={timezone.value === value}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{timezone.label}</span>
                    <span className="text-xs text-gray-500">{timezone.value}</span>
                  </div>
                </button>
              ))
            )}
          </div>

          {searchTerm && filteredTimezones.length > 0 && (
            <div className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500 bg-gray-50">
              {t('availability:timezone.timezonesFound', '{{count}} timezone found', { count: filteredTimezones.length })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BorderlessTimezoneSelector;